import type { CsmDecliningCompany } from '../../../api/dashboard';
import { clampDrop, formatPctChange } from './pctChange';

/** The comparison that flagged the company (mirrors the API's DeclineTrigger). */
export interface DeclineTrigger {
  metric: 'events' | 'tokens';
  window: '7d' | '30d';
  changePct: number;
  current: number;
  baseline: number;
}

export const pctText = formatPctChange;

/**
 * Tokens on Declining / At risk are always Classic + aOS. Said once in the
 * column headers and the info tip, not on every row.
 */
export const TOKENS_LABEL = 'Tokens';

/**
 * The other metric in the trigger's window, so a row always shows both logins
 * and tokens: "Tokens +1,260%" next to a login drop.
 */
export function otherSignal(c: CsmDecliningCompany, t: DeclineTrigger): { label: string; changePct: number | null } {
  if (t.metric === 'events') {
    return t.window === '30d'
      ? { label: TOKENS_LABEL, changePct: clampDrop(c.monthlyTokensChangePct, c.tokens30d) }
      : { label: TOKENS_LABEL, changePct: clampDrop(c.weeklyTokensChangePct, c.tokens7d) };
  }
  return t.window === '30d'
    ? { label: 'Login events', changePct: clampDrop(c.monthlyEventsChangePct, c.events30d) }
    : { label: 'Login events', changePct: clampDrop(c.weeklyEventsChangePct, c.events7d) };
}

export type GroupId = CsmDecliningCompany['severity'] | 'no_contract' | 'watch';

/**
 * Pure: which group a row goes in. A flagged company with no MRR this month
 * goes under "no live contract" instead of its severity, but only once the MRR
 * book has loaded (otherwise every row would look contract-less). Exported for tests.
 */
export function groupOf(c: CsmDecliningCompany, opts: { watch: boolean; mrrKnown: boolean; mrr: number | null }): GroupId {
  if (opts.watch) return 'watch';
  if (opts.mrrKnown && !opts.mrr) return 'no_contract';
  return c.severity;
}


/** The window a comparison covers, in words: "30d vs prior 30d", "7d vs 4-week avg". */
export const COMPARISON_TEXT: Record<DeclineTrigger['window'], string> = {
  '7d': '7d vs 4-week avg',
  '30d': '30d vs prior 30d',
};

/** The detector's thresholds (DEFAULT_DECLINE_THRESHOLDS / WATCH_TOKEN_RISE_PCT in detect-decline.ts). */
const WATCH_TOKEN_RISE_PCT = 25;
const EARLY_WARNING_DROP_PCT = 40;
const CONFIRMED_DROP_PCT = 30;

/**
 * Why a login drop with rising tokens is still a decline, not a watch. The
 * detector keeps it when tokens collapsed in the other window: Arendt (13 Oct
 * 2026) is login events −37% over 30 days with tokens +1,275% over 30 days, but
 * tokens −49% in the last 7 days vs the 4 weeks before. Returns "tokens −49%
 * 7d vs 4-week avg", or null when the row needs no explanation. Exported for tests.
 */
export function keptDeclineReason(c: CsmDecliningCompany, t: DeclineTrigger | null): string | null {
  if (!t || t.metric !== 'events') return null;
  const same = t.window === '30d' ? c.monthlyTokensChangePct : c.weeklyTokensChangePct;
  if (same === null || same < WATCH_TOKEN_RISE_PCT) return null;
  const other = t.window === '30d' ? clampDrop(c.weeklyTokensChangePct, c.tokens7d) : clampDrop(c.monthlyTokensChangePct, c.tokens30d);
  const limit = t.window === '30d' ? EARLY_WARNING_DROP_PCT : CONFIRMED_DROP_PCT;
  if (other === null || other > -limit) return null;
  return `tokens ${pctText(other)} ${t.window === '30d' ? '7d vs 4-week avg' : '30d vs prior 30d'}`;
}
